import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { RoleBadge } from "@/components/Navbar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Trash2, DatabaseBackup, ExternalLink, Loader2 } from "lucide-react";
import { ExternalAccessPanel } from "@/components/ExternalAccessPanel";

const ROLES = ["Admin", "Manager", "Employee"];
const OWNER_EMAIL = "g.radek@path.com.pl";

export default function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [backup, setBackup] = useState({ last: null, owner: "" });
  const [runningBackup, setRunningBackup] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  const loadBackup = () => api.get("/admin/backup-status").then((r) => setBackup(r.data)).catch(() => {});
  useEffect(() => { load(); loadBackup(); }, []);

  const changeRole = async (u, role) => {
    try {
      await api.put(`/users/${u.user_id}/role`, { role });
      toast.success(`${u.name} is now ${role}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update role");
    }
  };

  const removeUser = async (u) => {
    try {
      await api.delete(`/users/${u.user_id}`);
      toast.success(`Usunięto użytkownika: ${u.name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nie udało się usunąć użytkownika");
    }
  };

  const runBackup = async () => {
    setRunningBackup(true);
    try {
      const r = await api.post("/admin/backup-now");
      toast.success("Kopia zapasowa utworzona na Google Drive");
      setBackup((b) => ({ ...b, last: r.data }));
      loadBackup();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nie udało się utworzyć kopii");
    } finally {
      setRunningBackup(false);
    }
  };

  const connectGoogle = async () => {
    try {
      const r = await api.get("/google/connect");
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nie udało się rozpocząć łączenia z Google");
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <h1 className="font-[Outfit] font-bold tracking-tight text-4xl sm:text-5xl">User Management</h1>
        <p className="text-base text-muted-foreground font-[Manrope] mt-2">
          Assign roles across the Path team. {users.length} team member{users.length !== 1 ? "s" : ""}.
        </p>

        <div className="mt-8 border border-border bg-white rounded-md overflow-x-auto" data-testid="users-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50">
                <TableHead className="font-[Manrope]">User</TableHead>
                <TableHead className="font-[Manrope]">Email</TableHead>
                <TableHead className="font-[Manrope]">Region</TableHead>
                <TableHead className="font-[Manrope]">Role</TableHead>
                <TableHead className="font-[Manrope] text-right">Change Role</TableHead>
                <TableHead className="font-[Manrope] text-right">Remove</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => {
                const isOwner = u.email === OWNER_EMAIL;
                const initials = u.name?.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
                return (
                  <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8 border border-border">
                          <AvatarImage src={u.picture} alt={u.name} />
                          <AvatarFallback className="bg-zinc-100 text-xs">{initials}</AvatarFallback>
                        </Avatar>
                        <span className="font-[Manrope] font-medium">{u.name}</span>
                        {isOwner && <span className="text-xs text-[#193962] font-[Manrope]">(Owner)</span>}
                      </div>
                    </TableCell>
                    <TableCell className="font-[Manrope] text-muted-foreground text-sm">{u.email}</TableCell>
                    <TableCell className="font-[Manrope] text-sm">{u.region || "—"}</TableCell>
                    <TableCell><RoleBadge role={u.role} /></TableCell>
                    <TableCell className="text-right">
                      <Select
                        value={u.role}
                        onValueChange={(role) => changeRole(u, role)}
                        disabled={isOwner || u.user_id === user?.user_id || u.role === "Accountant"}
                      >
                        <SelectTrigger
                          data-testid={`role-select-${u.user_id}`}
                          className="w-36 ml-auto bg-white"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {[...ROLES, ...(u.role === "Accountant" ? ["Accountant"] : [])].map((r) => (
                            <SelectItem key={r} value={r} data-testid={`role-opt-${u.user_id}-${r}`}>{r}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      {isOwner || u.user_id === user?.user_id ? (
                        <span className="text-xs text-muted-foreground font-[Manrope]">—</span>
                      ) : (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`delete-user-btn-${u.user_id}`} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-white" data-testid={`delete-user-dialog-${u.user_id}`}>
                            <AlertDialogHeader>
                              <AlertDialogTitle className="font-[Outfit]">Usunąć użytkownika {u.name}?</AlertDialogTitle>
                              <AlertDialogDescription className="font-[Manrope]">
                                Ta operacja trwale usunie konto <strong>{u.email}</strong> z CRM oraz jego sesje i połączenie z Google. Klienci i spotkania pozostaną nienaruszone. Tej akcji nie można cofnąć.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel data-testid={`delete-user-cancel-${u.user_id}`}>Anuluj</AlertDialogCancel>
                              <AlertDialogAction data-testid={`delete-user-confirm-${u.user_id}`} onClick={() => removeUser(u)} className="bg-red-600 hover:bg-red-700 text-white">
                                Usuń użytkownika
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <div className="mt-10 border border-border bg-white rounded-md p-6" data-testid="backup-section">
          <div className="flex items-center gap-2.5">
            <DatabaseBackup className="h-5 w-5 text-[#193962]" />
            <h2 className="font-[Outfit] font-semibold text-lg">Kopie zapasowe (Google Drive)</h2>
          </div>
          <p className="text-sm text-muted-foreground font-[Manrope] mt-1">
            Codzienny automatyczny eksport danych CRM (klienci, kampanie, spotkania, leady, użytkownicy…) do folderu „Toren PATH CRM — Backups" na Google Drive konta <strong>{backup.owner || "b.carson@path.com.pl"}</strong>. Trzymane są ostatnie 30 kopii. Sekrety (tokeny, sesje) nie są zapisywane.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button onClick={runBackup} disabled={runningBackup} data-testid="backup-now-btn" className="bg-[#193962] hover:bg-[#12294A] text-white transition-all duration-200 hover:-translate-y-[1px]">
              {runningBackup ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Tworzę…</>) : (<><DatabaseBackup className="h-4 w-4 mr-2" />Utwórz kopię teraz</>)}
            </Button>
            <Button variant="outline" onClick={connectGoogle} data-testid="backup-connect-google-btn">Połącz konto Google (dla backupu)</Button>
          </div>
          <div className="mt-4 text-sm font-[Manrope]" data-testid="backup-last">
            {backup.last ? (
              <div className="flex flex-wrap items-center gap-2 text-muted-foreground">
                <span>Ostatnia kopia: <strong className="text-foreground">{new Date(backup.last.at).toLocaleString("pl-PL")}</strong></span>
                {backup.last.bytes ? <span>· {(backup.last.bytes / 1024).toFixed(0)} KB</span> : null}
                {backup.last.link && <a href={backup.last.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#193962] hover:underline" data-testid="backup-last-link">Otwórz w Google Drive <ExternalLink className="h-3.5 w-3.5" /></a>}
              </div>
            ) : (
              <p className="text-muted-foreground">Brak kopii jeszcze. Upewnij się, że <strong>b.carson@path.com.pl</strong> zalogował się do CRM i połączył konto Google (przycisk powyżej), a następnie kliknij „Utwórz kopię teraz".</p>
            )}
          </div>
        </div>
        <ExternalAccessPanel />
      </main>
    </div>
  );
}
